import { useState, useEffect, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { healthTestsApi, type HealthTest, type CreateHealthTestDto, type HealthTestFilters } from '../api/health-tests';
import { birdsApi, type Bird } from '../api/birds';
import { format } from 'date-fns';
import AddHealthTestModal from '../components/health-tests/AddHealthTestModal';
import EditHealthTestModal from '../components/health-tests/EditHealthTestModal';
import ViewHealthTestModal from '../components/health-tests/ViewHealthTestModal';
import HealthTestBirdInfo from '../components/health-tests/HealthTestBirdInfo';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import AmountInput from '../components/ui/AmountInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';

export default function HealthTests({ embedded = false, initialBirdId }: { embedded?: boolean; initialBirdId?: string } = {}) {
  // Data state
  const [healthTests, setHealthTests] = useState<HealthTest[]>([]);
  const [birds, setBirds] = useState<Bird[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<HealthTestFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof HealthTest>('testDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedHealthTest, setSelectedHealthTest] = useState<HealthTest | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (initialBirdId) {
      setFilters((prev) => ({ ...prev, birdId: initialBirdId }));
    }
  }, [initialBirdId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [healthTestsResponse, birdsResponse] = await Promise.all([
        healthTestsApi.getAll(),
        birdsApi.getAll()
      ]);
      setHealthTests(healthTestsResponse.data);
      setBirds(Array.isArray(birdsResponse.data) ? birdsResponse.data : (birdsResponse.data as any)?.items || []);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 
        typeof err === 'object' && err !== null ? JSON.stringify(err) : 
        'Unknown error occurred';
      setError(`Failed to load health test data: ${errorMessage}`);
      console.error('Error loading health test data:', err);
      toast.error(`Failed to load health test data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredHealthTests = useMemo(() => {
    let result = healthTests;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (test) =>
          test.testName.toLowerCase().includes(term) ||
          test.testeeName.toLowerCase().includes(term) ||
          test.result.toLowerCase().includes(term) ||
          test.bird?.bandId?.toLowerCase().includes(term) ||
          test.bird?.species?.commonName?.toLowerCase().includes(term) ||
          test.comment?.toLowerCase().includes(term) ||
          test.description?.toLowerCase().includes(term) ||
          test.notes?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.testName) {
      result = result.filter((test) => test.testName.toLowerCase().includes(filters.testName!.toLowerCase()));
    }
    if (filters.testeeName) {
      result = result.filter((test) => test.testeeName.toLowerCase().includes(filters.testeeName!.toLowerCase()));
    }
    if (filters.result) {
      result = result.filter((test) => test.result.toLowerCase().includes(filters.result!.toLowerCase()));
    }
    if (filters.birdId) {
      result = result.filter((test) => test.birdId === filters.birdId);
    }
    if (filters.dateRange) {
      result = result.filter((test) => {
        const testDate = new Date(test.testDate);
        const startDate = new Date(filters.dateRange!.start);
        const endDate = new Date(filters.dateRange!.end);
        return testDate >= startDate && testDate <= endDate;
      });
    }
    if (filters.costRange) {
      result = result.filter((test) => 
        test.cost >= filters.costRange!.min && test.cost <= filters.costRange!.max
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle nested properties
      if (sortBy === 'bird' && a.bird && b.bird) {
        aVal = a.bird.bandId;
        bVal = b.bird.bandId;
      }
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [healthTests, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredHealthTests.length / itemsPerPage);
  const paginatedHealthTests = filteredHealthTests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueTestNames = [...new Set(healthTests.map((test) => test.testName).filter(Boolean))];
  const uniqueTestees = [...new Set(healthTests.map((test) => test.testeeName).filter(Boolean))];
  const uniqueResults = [...new Set(healthTests.map((test) => test.result).filter(Boolean))];

  // Event handlers
  const handleAddHealthTest = async (data: CreateHealthTestDto) => {
    try {
      await healthTestsApi.create(data);
      await loadData();
      setShowAddModal(false);
      toast.success('Health test added successfully');
    } catch (error) {
      console.error('Error adding health test:', error);
      toast.error('Failed to add health test');
    }
  };

  const handleEditHealthTest = async (id: string, data: Partial<HealthTest>) => {
    try {
      await healthTestsApi.update(id, data);
      await loadData();
      setShowEditModal(false);
      setSelectedHealthTest(null);
      toast.success('Health test updated successfully');
    } catch (error) {
      console.error('Error updating health test:', error);
      toast.error('Failed to update health test');
    }
  };

  const handleDeleteHealthTest = async () => {
    if (!selectedHealthTest) return;
    
    try {
      await healthTestsApi.delete(selectedHealthTest.id);
      await loadData();
      setShowDeleteModal(false);
      setSelectedHealthTest(null);
      toast.success('Health test deleted successfully');
    } catch (error) {
      console.error('Error deleting health test:', error);
      toast.error('Failed to delete health test');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
  };

  const openViewModal = (test: HealthTest) => {
    setSelectedHealthTest(test);
    setShowViewModal(true);
  };

  const openEditModal = (test: HealthTest) => {
    setSelectedHealthTest(test);
    setShowEditModal(true);
  };

  const openDeleteModal = (test: HealthTest) => {
    setSelectedHealthTest(test);
    setShowDeleteModal(true);
  };

  const getResultBadge = (result: string) => {
    const normalResults = ['negative', 'normal', 'within normal limits'];
    const abnormalResults = ['positive', 'abnormal', 'elevated', 'low', 'critical'];
    const pendingResults = ['pending', 'inconclusive'];
    
    const resultLower = result.toLowerCase();
    
    if (normalResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">{result}</span>;
    }
    if (abnormalResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">{result}</span>;
    }
    if (pendingResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">{result}</span>;
    }
    
    return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">{result}</span>;
  };

  const getHealthTestStats = () => {
    const totalTests = filteredHealthTests.length;
    const totalCost = filteredHealthTests.reduce((sum, test) => {
      const cost = typeof test.cost === 'number' ? test.cost : parseFloat(test.cost as any);
      return sum + (!isNaN(cost) ? cost : 0);
    }, 0);
    const recentTests = filteredHealthTests.filter(test => {
      const testDate = new Date(test.testDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return testDate >= thirtyDaysAgo;
    }).length;
    const pendingTests = filteredHealthTests.filter(test => 
      test.result.toLowerCase().includes('pending') || 
      test.result.toLowerCase().includes('inconclusive')
    ).length;

    return { totalTests, totalCost, recentTests, pendingTests };
  };

  const stats = getHealthTestStats();

  if (loading) {
    if (embedded) {
      return (
        <div className="flex h-48 items-center justify-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50/80">
        <div className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
              <p className="mt-2 text-sm text-slate-500">Loading health tests...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    if (embedded) {
      return (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={loadData} className="ml-2 font-medium text-red-600 hover:text-red-500">
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50/80">
        <div className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
              <button
                onClick={loadData}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
          <PageHero
            title="Health Tests"
            subtitle="Track and manage health tests and medical records for your birds"
            actions={
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Health Test
              </button>
            }
          />
      )}

      {embedded && (
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Health tests</h3>
            <p className="text-sm text-slate-500">Search, filter, and manage all test records.</p>
          </div>
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary text-sm">
            <PlusIcon className="h-4 w-4" />
            Add test
          </button>
        </div>
      )}

      {/* Stats */}
      <div className={embedded ? 'mt-2' : 'mt-8'}>
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Total Tests</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{stats.totalTests}</dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Total Cost</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-green-600">
                  R{(typeof stats.totalCost === 'number' && !isNaN(stats.totalCost) ? stats.totalCost : 0).toFixed(2)}
                </dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Recent Tests (30 days)</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-cyan-600">{stats.recentTests}</dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Pending Results</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-yellow-600">{stats.pendingTests}</dd>
              </div>
            </dl>
          </div>

          {/* Search and filters */}
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 items-center gap-x-4">
              <div className="flex items-center flex-1 max-w-md rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
                <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search health tests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
              >
                <FunnelIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                Filters
                {Object.keys(filters).length > 0 && (
                  <span className="ml-1 rounded-full bg-cyan-600 px-2 py-0.5 text-xs text-white">
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-x-4">
              {/* Sort */}
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as keyof HealthTest);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="rounded-md border-0 py-1.5 pl-3 pr-10 text-slate-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-cyan-500 sm:text-sm sm:leading-6"
              >
                <option key="testDate-desc-1753461297379" value="testDate-desc">Newest First</option>
                <option key="testDate-asc-1753461297379" value="testDate-asc">Oldest First</option>
                <option key="testName-asc-1753461297379" value="testName-asc">Test Name A-Z</option>
                <option key="testName-desc-1753461297379" value="testName-desc">Test Name Z-A</option>
                <option key="cost-desc-1753461297379" value="cost-desc">Highest Cost</option>
                <option key="cost-asc-1753461297379" value="cost-asc">Lowest Cost</option>
              </select>
              
              {/* View mode toggle */}
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                    viewMode === 'grid'
                      ? 'bg-cyan-600 text-white hover:bg-cyan-500'
                      : 'bg-white text-slate-900'
                  }`}
                >
                  <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                    viewMode === 'table'
                      ? 'bg-cyan-600 text-white hover:bg-cyan-500'
                      : 'bg-white text-slate-900'
                  }`}
                >
                  <TableCellsIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 rounded-lg bg-white p-6 shadow">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Test Name</label>
                  <select
                    value={filters.testName || ''}
                    onChange={(e) => setFilters({ ...filters, testName: e.target.value })}
                    className="mt-1 input text-sm"
                  >
                    <option key="empty-option-1753461297379" value="">All Tests</option>
                    {uniqueTestNames.map((testName) => (
                      <option key={testName} value={testName}>
                        {testName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700">Tested By</label>
                  <select
                    value={filters.testeeName || ''}
                    onChange={(e) => setFilters({ ...filters, testeeName: e.target.value })}
                    className="mt-1 input text-sm"
                  >
                    <option key="empty-option-1753461297379" value="">All Testees</option>
                    {uniqueTestees.map((testeeName) => (
                      <option key={testeeName} value={testeeName}>
                        {testeeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Result</label>
                  <select
                    value={filters.result || ''}
                    onChange={(e) => setFilters({ ...filters, result: e.target.value })}
                    className="mt-1 input text-sm"
                  >
                    <option key="empty-option-1753461297379" value="">All Results</option>
                    {uniqueResults.map((result) => (
                      <option key={result} value={result}>
                        {result}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Bird</label>
                  <select
                    value={filters.birdId || ''}
                    onChange={(e) => setFilters({ ...filters, birdId: e.target.value })}
                    className="mt-1 input text-sm"
                  >
                    <option key="empty-option-1753461297379" value="">All Birds</option>
                    {birds.map((bird) => (
                      <option key={bird.id} value={bird.id}>
                        {bird.bandId} - {bird.species?.commonName || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Date From</label>
                  <input
                    type="date"
                    value={filters.dateRange?.start || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value, end: filters.dateRange?.end || '' }
                    })}
                    className="input mt-1 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Date To</label>
                  <input
                    type="date"
                    value={filters.dateRange?.end || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: filters.dateRange?.start || '', end: e.target.value }
                    })}
                    className="input mt-1 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Min Cost (R)</label>
                  <AmountInput
                    value={filters.costRange?.min ?? ''}
                    onChange={(v) => setFilters({
                      ...filters,
                      costRange: { ...filters.costRange, min: v ?? 0, max: filters.costRange?.max || 0 }
                    })}
                    allowEmpty
                    min={0}
                    inputClassName="input mt-1 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Max Cost (R)</label>
                  <AmountInput
                    value={filters.costRange?.max ?? ''}
                    onChange={(v) => setFilters({
                      ...filters,
                      costRange: { ...filters.costRange, min: filters.costRange?.min || 0, max: v ?? 0 }
                    })}
                    allowEmpty
                    min={0}
                    inputClassName="input mt-1 sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex justify-end gap-x-3">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
                >
                  <XMarkIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {paginatedHealthTests.length === 0 ? (
            <EmptyState
              title="No health tests found"
              description={
                searchTerm || Object.keys(filters).length > 0
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first health test.'
              }
              icon={<HeartIcon className="h-6 w-6" />}
              action={
                !searchTerm && Object.keys(filters).length === 0 ? (
                  <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                    <PlusIcon className="h-5 w-5" />
                    Add Health Test
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {paginatedHealthTests.map((test) => (
                    <div
                      key={test.id}
                      className="overflow-hidden rounded-lg bg-white shadow hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <HeartIcon className="h-8 w-8 text-cyan-600" />
                            <div className="ml-3">
                              <h3 className="text-sm font-semibold text-slate-900">
                                {test.testName}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {test.bird?.bandId || 'Unknown Bird'}
                              </p>
                            </div>
                          </div>
                          {test.result && getResultBadge(test.result)}
                        </div>
                        
                        <div className="mt-4">
                          <dl className="grid grid-cols-2 gap-4">
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Date</dt>
                              <dd className="text-sm text-slate-900">{format(new Date(test.testDate), 'MMM dd, yyyy')}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Cost</dt>
                              <dd className="text-sm text-slate-900">
                                R{(() => {
                                  const cost = typeof test.cost === 'number' ? test.cost : parseFloat(test.cost as any);
                                  return !isNaN(cost) ? cost.toFixed(2) : '0.00';
                                })()}
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <div className="mt-4">
                          <p className="text-sm text-slate-500">Tested by: {test.testeeName}</p>
                        </div>

                        <div className="mt-6 flex justify-between">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openViewModal(test)}
                              className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(test)}
                              className="inline-flex items-center rounded-md bg-cyan-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <button
                            onClick={() => openDeleteModal(test)}
                            className="inline-flex items-center rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === 'table' && (
                <div className="mt-8 flow-root">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-slate-50/80">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Test Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Bird
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Tested By
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Result
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Cost
                              </th>
                              <th className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {paginatedHealthTests.map((test) => (
                              <tr key={test.id} className="hover:bg-slate-50/80">
                                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-slate-900">
                                  {test.testName}
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-900">
                                  {test.bird ? (
                                    <HealthTestBirdInfo 
                                      bird={test.bird} 
                                      layout="horizontal"
                                      showDetails={false}
                                    />
                                  ) : (
                                    <div className="text-slate-500">Unknown Bird</div>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {format(new Date(test.testDate), 'MMM dd, yyyy')}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {test.testeeName}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {test.result ? getResultBadge(test.result) : 'No result'}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  R{(() => {
                                    const cost = typeof test.cost === 'number' ? test.cost : parseFloat(test.cost as any);
                                    return !isNaN(cost) ? cost.toFixed(2) : '0.00';
                                  })()}
                                </td>
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => openViewModal(test)}
                                      className="text-cyan-600 hover:text-indigo-900"
                                      title="View details"
                                    >
                                      <EyeIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => openEditModal(test)}
                                      className="text-cyan-600 hover:text-indigo-900"
                                      title="Edit test"
                                    >
                                      <PencilIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => openDeleteModal(test)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete test"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-slate-700">
                        Showing{' '}
                        <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * itemsPerPage, filteredHealthTests.length)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{filteredHealthTests.length}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => {
                          const page = i + 1;
                          const isCurrentPage = page === currentPage;
                          
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 2 && page <= currentPage + 2)
                          ) {
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  isCurrentPage
                                    ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                                    : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:outline-offset-0'
                                } focus:z-20`}
                              >
                                {page}
                              </button>
                            );
                          }
                          
                          if (page === currentPage - 3 || page === currentPage + 3) {
                            return (
                              <span key={page} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                ...
                              </span>
                            );
                          }
                          
                          return null;
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

      {/* Modals */}
      <AddHealthTestModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddHealthTest}
        birds={birds}
      />

      <EditHealthTestModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditHealthTest}
        healthTest={selectedHealthTest}
        birds={birds}
      />

      <ViewHealthTestModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        healthTest={selectedHealthTest}
        birds={birds}
      />

      {/* Delete Confirmation Modal */}
      <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-md">
        <BrandModalHeader title="Delete Health Test" onClose={() => setShowDeleteModal(false)} />
        <BrandModalBody>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete the health test &quot;{selectedHealthTest?.testName}&quot; for {selectedHealthTest?.bird?.bandId}?
            This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => setShowDeleteModal(false)}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteHealthTest}
            className="btn btn-danger text-sm"
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-slate-50/80">
      <div className="py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{content}</div>
      </div>
    </div>
  );
}
